import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../tokens';

const badgeVariants = {
  primary: css`
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[700]};
  `,
  secondary: css`
    background: ${theme.colors.secondary[100]};
    color: ${theme.colors.secondary[700]};
  `,
  success: css`
    background: ${theme.colors.success[100]};
    color: ${theme.colors.success[700]};
  `,
  error: css`
    background: ${theme.colors.error[100]};
    color: ${theme.colors.error[700]};
  `,
  warning: css`
    background: ${theme.colors.warning[100]};
    color: ${theme.colors.warning[700]};
  `,
  premium: css`
    background: ${theme.colors.premium[100]};
    color: ${theme.colors.premium[700]};
  `,
  neutral: css`
    background: ${theme.colors.neutral[100]};
    color: ${theme.colors.neutral[700]};
  `,
  // Solid variants
  solidPrimary: css`
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.text.inverse};
  `,
  solidSuccess: css`
    background: ${theme.colors.success[500]};
    color: ${theme.colors.text.inverse};
  `,
  solidError: css`
    background: ${theme.colors.error[500]};
    color: ${theme.colors.text.inverse};
  `,
  solidWarning: css`
    background: ${theme.colors.warning[500]};
    color: ${theme.colors.text.inverse};
  `,
  solidPremium: css`
    background: ${theme.colors.premium[500]};
    color: ${theme.colors.text.inverse};
  `,
  // Outline variants
  outlinePrimary: css`
    background: transparent;
    color: ${theme.colors.primary[500]};
    border: 1px solid ${theme.colors.primary[500]};
  `,
  outlineSuccess: css`
    background: transparent;
    color: ${theme.colors.success[500]};
    border: 1px solid ${theme.colors.success[500]};
  `,
  outlineError: css`
    background: transparent;
    color: ${theme.colors.error[500]};
    border: 1px solid ${theme.colors.error[500]};
  `,
};

const badgeSizes = {
  xs: css`
    height: 18px;
    padding: 0 ${theme.spacing[1.5]};
    font-size: 10px;
  `,
  sm: css`
    height: 22px;
    padding: 0 ${theme.spacing[2]};
    font-size: ${theme.fontSizes.xs};
  `,
  md: css`
    height: 26px;
    padding: 0 ${theme.spacing[2.5]};
    font-size: ${theme.fontSizes.xs};
  `,
  lg: css`
    height: 32px;
    padding: 0 ${theme.spacing[3]};
    font-size: ${theme.fontSizes.sm};
  `,
};

const StyledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[1]};
  font-family: ${theme.fontFamilies.sans};
  font-weight: ${theme.fontWeights.medium};
  border-radius: ${({ $rounded }) => ($rounded ? theme.radii.full : theme.radii.md)};
  white-space: nowrap;
  border: 1px solid transparent;

  ${({ $variant }) => badgeVariants[$variant] || badgeVariants.neutral}
  ${({ $size }) => badgeSizes[$size] || badgeSizes.md}

  ${({ $dot }) =>
    $dot &&
    css`
      &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }
    `}

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const Badge = React.forwardRef(
  ({ children, variant = 'neutral', size = 'md', rounded = false, dot = false, leftIcon, rightIcon, ...props }, ref) => {
    return (
      <StyledBadge ref={ref} $variant={variant} $size={size} $rounded={rounded} $dot={dot} {...props}>
        {leftIcon}
        {children}
        {rightIcon}
      </StyledBadge>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;
